// "Akses event" — restrict an event to named staff.
//
// Lives inside the edit-event dialog for now; batch 4's event detail page
// gives it a tab of its own. Saved on its own button rather than with the
// form: it is a different kind of change (who, not what) and the form's
// submit is guarded by the event lifecycle, which this is not.

import { useEffect, useMemo, useState } from 'react';
import { Users, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUsers } from '@/hooks/useUsers';
import { useEventUsers, useSetEventUsers } from '@/hooks/useEventUsers';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/format';

export function EventAccessSection({ eventId }: { eventId: string }) {
  const { can } = usePermissions();
  const editable = can('events', 'edit');
  const { data: users } = useUsers();
  const { data: access, isLoading } = useEventUsers(eventId);
  const save = useSetEventUsers();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (access) setSelected(new Set(access.map((a) => a.userId)));
  }, [access]);

  const staff = useMemo(
    () => (users ?? []).filter((u) => u.isActive).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users],
  );
  const dirty = useMemo(() => {
    const now = new Set(access?.map((a) => a.userId) ?? []);
    if (now.size !== selected.size) return true;
    for (const id of selected) if (!now.has(id)) return true;
    return false;
  }, [access, selected]);

  const toggle = (uid: string) => {
    setSaved(false);
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid);
      else n.add(uid);
      return n;
    });
  };

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ eventId, userIds: [...selected] });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan akses');
    }
  };

  return (
    <section className="pt-4 mt-4 border-t border-line" data-tour="event-access">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h4 className="text-body font-display font-semibold text-ink inline-flex items-center gap-2">
          <Users size={16} className="text-ink-subtle" />
          Akses event
        </h4>
        {selected.size > 0 && (
          <span className="text-caption text-ink-subtle">{selected.size} orang</span>
        )}
      </div>
      <p className="text-caption text-ink-subtle mb-3">
        Kosong = semua staf (sesuai rayon) bisa melihat event ini. Dicentang = <b>hanya</b> yang
        dicentang yang melihat event, transaksi, dan stoknya. Yang punya hak <i>events.edit</i>{' '}
        selalu bisa.
      </p>

      {isLoading ? (
        <p className="text-caption text-ink-subtle">Memuat…</p>
      ) : (
        <ul className="grid gap-1.5 sm:grid-cols-2 max-h-56 overflow-y-auto pr-1">
          {staff.map((u) => {
            const on = selected.has(u.uid);
            return (
              <li key={u.uid}>
                <label
                  className={cn(
                    'flex items-center gap-2 px-2.5 py-2 rounded border text-small cursor-pointer',
                    on ? 'border-primary bg-primary/5' : 'border-line hover:bg-canvas',
                    !editable && 'cursor-default opacity-70',
                  )}
                >
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={on}
                    disabled={!editable}
                    onChange={() => toggle(u.uid)}
                  />
                  <span className="min-w-0">
                    <span className="block text-ink truncate">{u.displayName}</span>
                    <span className="block text-caption text-ink-subtle truncate">
                      {u.roleId} · {u.email}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {editable && (
        <div className="flex items-center gap-3 mt-3">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Save size={14} />}
            onClick={submit}
            loading={save.isPending}
            disabled={!dirty}
          >
            Simpan akses
          </Button>
          {saved && !dirty && <span className="text-caption text-success">Tersimpan.</span>}
          {error && <span role="alert" className="text-caption text-danger">{error}</span>}
        </div>
      )}
    </section>
  );
}
